import { Injectable, OnDestroy, PLATFORM_ID, Inject } from '@angular/core';
import { Subject } from 'rxjs';
import { WindowRefService } from './window/window.service';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';

@Injectable({
  providedIn: 'root'
})

/**
 * The config object in this service will hold the below information
 *
 * rc                   - Region code
 * lc                   - Language code
 * locale               - rc-lc
 * language             - Route's :lang param (eg. us-en)
 * languageDirection    - Set based on rc-lc
 * domain               - From common service - window.location.origin
 * templateName         - Route's templateName
 * pageName             - From router's data
 * userAgent            - From DeviceDetectorService
 * os                   - Operating system
 * queryParams          - Query params object
 * routeParams          - Route params object
 * routeData            - Route data object
 */
export class PageService implements OnDestroy {
  config: any = {
    languageDirection: 'ltr',
    isNavAuthenticated: false,
  };

  private window: Window;

  destroySubject$: Subject<void> = new Subject();

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private windowRef: WindowRefService,
    private router: Router,
    private titleService: Title
  ) {
    this.window = this.windowRef.nativeWindow;
  }

  setTitle(newTitle) {
    this.titleService.setTitle(newTitle);
  }

  getDataFromQueryParams(queryParams) {
    this.config.queryParams = queryParams;
  }

  getDataFromRouteParams(routeParams: any) {
    const params = routeParams;
    this.config.routeParams = params;
    this.config.language = params['lang'];
    this.config.rc = this.getRcLc('rc').toLowerCase();
    this.config.lc = this.getRcLc('lc').toLowerCase();
    this.config.locale = this.config.rc + '-' + this.config.lc;
    this.setDocumentLanguage();
    this.rclcValidation(this.config.rc, this.config.lc);
  }

  setDocumentLanguage() {
    if (this.config.isBrowser) {
      document.documentElement.lang = this.config.lc;
    }
  }

  getDataFromRouteData(routeData: { pageName: any; templateName: any }) {
    this.config.routeData = routeData;
    this.config.pageName = routeData.pageName;
    this.config.templateName = routeData.templateName;
  }

  // This method is called from AppLayoutComponent to initialize all page level configurations
  init(data: any) {
    this.config.getUrl = this.getUrl();
    this.config.domain = this.getDomain();
    this.getDataFromRouteParams(data.routeParams);
    this.getDataFromRouteData(data.routeData);
    this.getDataFromQueryParams(data.queryParams);
  }

  rclcValidation(rc, lc) {
    rc = rc.toLowerCase();
    lc = lc.toLowerCase();
    let rclcList = [];
    if (localStorage.getItem('rclcData') !== 'undefined') {
      rclcList = JSON.parse(localStorage.getItem('rclcData'));
    }
    if (!rclcList) {
      return;
    }
    const rcList = rclcList.filter(e => e.regionCode == rc);
    const lcListFromRC = rcList.filter(e => e.languageCode == lc)
    const validLC = lcListFromRC?.length ? true : false;

    const lcList = rclcList.filter(e => e.languageCode == lc);
    const rcListFromLC = lcList.filter(e => e.regionCode == rc)
    const validRC = rcListFromLC?.length ? true : false;

    const redirectTo404Page = (validLC || validRC) ? false : true;
    if (redirectTo404Page) {
      const channelPartnerFlag = this.window.location.pathname?.includes("wisechannelpartner") ? true : false;
      const path = channelPartnerFlag ? 'wisechannelpartner' : 'wise';
      const url = `/${path}/home/ams-en`;
      this.router.navigate([url]);
    }
  }

  // Moved from CommonService
  getUrl() {
    return typeof this.window !== 'undefined'
      ? this.window.location.href
      : '';
  }

  getDomain() {
    return typeof this.window !== 'undefined'
      ? this.window.location.origin
      : '';
  }

  getRcLc(rclc: string) {
    const params = this.config.routeParams;
    const splitVal = params.lang.split('-');
    const lcVal =
      splitVal.length === 3 ? splitVal[1] + '-' + splitVal[2] : splitVal[1];
    if (params && params.lang) {
      return rclc.trim().toLowerCase() === 'rc'
        ? params.lang.split('-')[0]
        : lcVal;
    } else if (typeof this.window !== 'undefined') {
      return rclc.trim().toLowerCase() === 'rc'
        ? this.window.location.pathname.split('/')[1].split('-')[0]
        : this.window.location.pathname.split('/')[1].split('-')[1];
    }
  }

  ngOnDestroy() {
    this.destroySubject$.next();
  }
}
